<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import {
  Check,
  CircleAlert,
  Disc3,
  FileMusic,
  LoaderCircle,
  Mic2,
  Music2,
  RefreshCw,
  Sparkles,
  WandSparkles,
} from "@lucide/vue";
import TrackArtwork from "@/components/TrackArtwork.vue";
import { api } from "@/lib/api";
import type { Track } from "@/types";

const title = ref("凌晨四点的霓虹");
const prompt = ref("华语独立流行，夜晚城市，克制而有力量，女声，温暖合成器，副歌有记忆点");
const lyrics = ref("");
const instrumental = ref(false);
const generatingLyrics = ref(false);
const generatingTitle = ref(false);
const generatingDescription = ref(false);
const generatingMusic = ref(false);
const error = ref("");
const result = ref<Track | null>(null);
const style = ref("城市流行");
const titleSuggestions = ref<string[]>([]);
const generatedTitleHistory = ref<string[]>([]);
const titleGenerationCount = ref(0);

type VocalStyle = "female-clear" | "female-powerful" | "male-warm" | "male-deep";

const vocalStyle = ref<VocalStyle>("female-clear");
const vocalStyles: Array<{
  id: VocalStyle;
  label: string;
  detail: string;
  prompt: string;
}> = [
  { id: "female-clear", label: "清透女声", detail: "轻盈 · 亲密", prompt: "清透温暖女声，轻盈且有亲密感" },
  { id: "female-powerful", label: "力量女声", detail: "宽广 · 爆发", prompt: "力量感女声，音域宽广，副歌有爆发力" },
  { id: "male-warm", label: "温暖男声", detail: "真诚 · 中低音", prompt: "温暖真诚男声，中低音饱满，演唱自然" },
  { id: "male-deep", label: "低沉男声", detail: "磁性 · 克制", prompt: "低沉磁性男声，声线有颗粒感，演唱克制" },
];

const presets = [
  { label: "城市流行", value: "华语独立流行，夜晚城市，克制而有力量，温暖合成器，副歌有记忆点" },
  { label: "松弛爵士", value: "Neo Soul，爵士和弦，慵懒鼓组，深夜酒吧，温柔女声" },
  { label: "热血摇滚", value: "华语摇滚，现场感鼓组，失真吉他，充满希望，万人合唱副歌" },
  { label: "氛围电子", value: "Ambient Electronic，未来感，颗粒合成器，宽广空间，纯音乐" },
];

const progress = computed(() =>
  generatingMusic.value ? 64 : result.value ? 100 : 12,
);

function choosePreset(item: (typeof presets)[number]) {
  style.value = item.label;
  instrumental.value = item.label === "氛围电子";
  prompt.value = withSelectedVocal(item.value);
}

function withSelectedVocal(value: string) {
  if (instrumental.value) return value;
  const selected = vocalStyles.find((item) => item.id === vocalStyle.value) || vocalStyles[0];
  const cleaned = value
    .replace(/\b(?:male|female)\s+vocals?\b/gi, "")
    .replace(/(?:清透|清亮|温柔|力量|高亢|温暖|低沉|磁性)?[男女]声(?:主唱|演唱|音色)?/g, "")
    .replace(/[，,]{2,}/g, "，")
    .replace(/^[，,\s]+|[，,\s]+$/g, "");
  return `${cleaned}，${selected.prompt}`;
}

function chooseVocal(id: VocalStyle) {
  vocalStyle.value = id;
  instrumental.value = false;
  prompt.value = withSelectedVocal(prompt.value);
}

async function generateTitle() {
  error.value = "";
  generatingTitle.value = true;
  try {
    const selected = presets.find((item) => item.label === style.value) || presets[0];
    style.value = selected.label;
    const data = await api.songTitle({
      prompt: withSelectedVocal(`${selected.label}。${prompt.value || selected.value}`),
      lyrics: lyrics.value || undefined,
      instrumental: instrumental.value,
      excludedTitles: [title.value, ...generatedTitleHistory.value].filter(Boolean),
      variation: titleGenerationCount.value,
    });
    title.value = data.title;
    prompt.value = withSelectedVocal(data.description);
    titleGenerationCount.value += 1;
    generatedTitleHistory.value = [
      data.title,
      ...generatedTitleHistory.value.filter((item) => item !== data.title),
    ].slice(0, 12);
    titleSuggestions.value = [
      data.title,
      ...titleSuggestions.value.filter((item) => item !== data.title),
    ].slice(0, 4);
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "AI 取名失败";
  } finally {
    generatingTitle.value = false;
  }
}

async function generateDescription() {
  error.value = "";
  generatingDescription.value = true;
  try {
    const selected = presets.find((item) => item.label === style.value) || presets[0];
    style.value = selected.label;
    const data = await api.songTitle({
      title: title.value || undefined,
      prompt: withSelectedVocal(`${selected.label}。${prompt.value || selected.value}`),
      lyrics: lyrics.value || undefined,
      instrumental: instrumental.value,
    });
    prompt.value = withSelectedVocal(data.description);
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "AI 生成描述失败";
  } finally {
    generatingDescription.value = false;
  }
}

async function createLyrics() {
  error.value = "";
  generatingLyrics.value = true;
  try {
    const data = await api.lyrics({
      prompt: `为歌曲《${title.value}》创作完整歌词。${prompt.value}`,
      title: title.value,
    });
    title.value = data.songTitle;
    lyrics.value = data.lyrics;
    if (data.styleTags) prompt.value = `${data.styleTags}。${prompt.value}`;
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "歌词生成失败";
  } finally {
    generatingLyrics.value = false;
  }
}

async function createMusic() {
  error.value = "";
  result.value = null;
  generatingMusic.value = true;
  try {
    const data = await api.music({
      title: title.value,
      prompt: prompt.value,
      lyrics: lyrics.value,
      instrumental: instrumental.value,
      vocalStyle: vocalStyle.value,
    });
    result.value = data.track;
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "音乐生成失败";
  } finally {
    generatingMusic.value = false;
  }
}

onMounted(() => {
  const draft = localStorage.getItem("wavecraft-lyrics-draft");
  if (draft) {
    try {
      const parsed = JSON.parse(draft);
      title.value = parsed.title || title.value;
      prompt.value = parsed.prompt || prompt.value;
      lyrics.value = parsed.lyrics || "";
      localStorage.removeItem("wavecraft-lyrics-draft");
    } catch {
      // 忽略损坏的设备草稿。
    }
  }
});
</script>

<template>
  <div class="creator-layout">
    <section class="creator-form panel">
      <div class="section-heading border-bottom">
        <div>
          <p class="eyebrow">01 / CREATIVE BRIEF</p>
          <h3>定义这首歌的灵魂</h3>
        </div>
        <span class="model-label"><span /> MUSIC-3.0</span>
      </div>

      <div class="form-section">
        <div class="field title-generator-field">
          <div class="title-field-heading">
            <label for="create-title">作品名 <small>{{ title.length }}/80</small></label>
            <button class="title-magic-button" type="button" :disabled="generatingTitle || generatingDescription" @click="generateTitle">
              <LoaderCircle v-if="generatingTitle" class="spin" :size="15" />
              <WandSparkles v-else :size="15" />
              {{ generatingTitle ? "AI 正在取名…" : titleSuggestions.length ? "换一个歌名" : "AI 生成歌名" }}
            </button>
          </div>
          <div class="title-input-shell" :class="{ loading: generatingTitle }">
            <input id="create-title" v-model="title" maxlength="80" placeholder="给这首歌一个名字" />
            <span class="title-spark" aria-hidden="true"><Sparkles :size="17" /></span>
          </div>
          <div v-if="titleSuggestions.length" class="title-suggestions" aria-label="AI 歌名候选">
            <span>AI 候选</span>
            <button
              v-for="item in titleSuggestions"
              :key="item"
              type="button"
              :class="{ active: title === item }"
              @click="title = item"
            >{{ item }}</button>
            <button class="title-refresh" type="button" :disabled="generatingTitle" aria-label="再生成一个歌名" @click="generateTitle"><RefreshCw :size="13" /></button>
          </div>
        </div>

        <div class="field">
          <span>快速风格</span>
          <div class="chip-row">
            <button
              v-for="item in presets"
              :key="item.label"
              type="button"
              class="choice-chip"
              :class="{ active: style === item.label }"
              @click="choosePreset(item)"
            >
              {{ item.label }}
            </button>
          </div>
        </div>

        <div class="field vocal-style-field" :class="{ muted: instrumental }">
          <div class="title-field-heading">
            <span>人声音色 <small>{{ instrumental ? "纯音乐模式已停用" : "选择主唱声线" }}</small></span>
          </div>
          <div class="vocal-style-grid">
            <button
              v-for="voice in vocalStyles"
              :key="voice.id"
              type="button"
              class="vocal-style-card"
              :class="{ active: vocalStyle === voice.id }"
              :disabled="instrumental"
              :aria-pressed="vocalStyle === voice.id"
              @click="chooseVocal(voice.id)"
            >
              <span class="vocal-icon"><Mic2 :size="17" /><i /><i /><i /></span>
              <span><strong>{{ voice.label }}</strong><small>{{ voice.detail }}</small></span>
              <Check v-if="vocalStyle === voice.id" class="vocal-check" :size="15" />
            </button>
          </div>
          <em>Music 通过演唱描述控制声线；男声选择会在服务端再次校验并写入生成指令。</em>
        </div>

        <div class="field description-generator-field">
          <div class="title-field-heading">
            <label for="music-description">音乐描述 <small>{{ prompt.length }}/2000</small></label>
            <button class="title-magic-button" type="button" :disabled="generatingDescription || generatingTitle" @click="generateDescription">
              <LoaderCircle v-if="generatingDescription" class="spin" :size="15" />
              <WandSparkles v-else :size="15" />
              {{ generatingDescription ? "正在构思描述…" : "AI 生成描述" }}
            </button>
          </div>
          <div class="description-input-shell" :class="{ loading: generatingDescription || generatingTitle }">
            <textarea id="music-description" v-model="prompt" maxlength="2000" rows="4" placeholder="风格、情绪、乐器、速度、场景……" />
            <span class="description-ai-badge" aria-hidden="true"><Sparkles :size="14" /> AI BRIEF</span>
          </div>
          <em>写得具体更好：曲风 + 情绪 + 乐器 + 人声 + 场景</em>
        </div>

        <div class="toggle-line">
          <span>
            <Music2 :size="18" />
            <span><strong>纯音乐模式</strong><small>关闭人声与歌词</small></span>
          </span>
          <button class="switch" :class="{ active: instrumental }" role="switch" :aria-checked="instrumental" @click="instrumental = !instrumental"><i /></button>
        </div>
      </div>

      <div class="form-section" :class="{ muted: instrumental }">
        <div class="field-heading">
          <span>歌词</span>
          <button class="magic-link" type="button" :disabled="instrumental || generatingLyrics" @click="createLyrics">
            <LoaderCircle v-if="generatingLyrics" class="spin" :size="15" />
            <WandSparkles v-else :size="15" />
            {{ generatingLyrics ? "正在写词…" : "AI 帮我写词" }}
          </button>
        </div>
        <textarea v-model="lyrics" class="lyrics-input" :disabled="instrumental" maxlength="3500" rows="12" placeholder="从第一句开始写下故事……&#10;&#10;用自然空行分隔主歌与副歌……" />
        <div class="char-line">
          <span>AI 会自动整理自然段，不显示方括号段落标签</span>
          <span>{{ lyrics.length }}/3500</span>
        </div>
      </div>

      <div v-if="error" class="error-banner">
        <CircleAlert :size="18" />
        <span>{{ error }} <router-link v-if="error.includes('口令')" to="/settings">去设置</router-link></span>
      </div>

      <button class="button button-dark button-wide generate-button" :disabled="generatingMusic || !title || !prompt" @click="createMusic">
        <LoaderCircle v-if="generatingMusic" class="spin" :size="19" />
        <Sparkles v-else :size="19" />
        {{ generatingMusic ? "正在编曲、演唱与保存（通常需要数分钟）" : "生成并保存完整音乐" }}
      </button>
      <p class="form-footnote">每次提交会占用一次 MiniMax 音乐生成额度；仅达到 ≥320 Kbps、≥44.1 kHz 的 WAV 母带才会进入曲库。</p>
    </section>

    <aside class="creator-preview">
      <article class="panel sticky-card">
        <div class="section-heading">
          <div>
            <p class="eyebrow">LIVE SESSION</p>
            <h3>本次创作</h3>
          </div>
          <span class="score-badge">{{ progress }}%</span>
        </div>
        <div class="progress-track"><i :style="{ width: `${progress}%` }" /></div>
        <div class="creation-steps">
          <div class="done"><span><Check :size="14" /></span><p><strong>创意简报</strong><small>提示词已就绪</small></p></div>
          <div :class="{ done: lyrics || instrumental, active: generatingLyrics }"><span><Check v-if="lyrics || instrumental" :size="14" /><Mic2 v-else :size="14" /></span><p><strong>歌词结构</strong><small>{{ instrumental ? "纯音乐已跳过" : lyrics ? "歌词已就绪" : "等待歌词" }}</small></p></div>
          <div :class="{ done: result, active: generatingMusic }"><span><Check v-if="result" :size="14" /><Disc3 v-else :size="14" /></span><p><strong>生成母带</strong><small>{{ generatingMusic ? "模型正在创作" : result ? "高音质 WAV 已保存" : "等待生成" }}</small></p></div>
          <div :class="{ done: result }"><span><FileMusic :size="14" /></span><p><strong>进入曲库</strong><small>{{ result ? "可试听与下载" : "自动完成" }}</small></p></div>
        </div>

        <div v-if="result" class="result-card">
          <TrackArtwork :title="result.title" />
          <div>
            <span class="success-label">生成成功</span>
            <h4>{{ result.title }}</h4>
            <p>{{ result.genre }} · {{ Math.ceil(result.durationMs / 1000) }} 秒 · {{ result.audioFormat.toUpperCase() }}</p>
          </div>
          <audio :src="result.audioUrl" controls autoplay />
          <div class="result-actions">
            <a class="button button-lime button-sm" :href="result.audioUrl" download>下载高音质 WAV</a>
            <router-link class="button button-ghost button-sm" to="/release">准备发行</router-link>
          </div>
        </div>

        <div v-else class="studio-note">
          <span><Sparkles :size="18" /></span>
          <p><strong>Music 3.0 创作提示</strong>指定乐器、演唱方式和段落变化，会比只写“流行歌”得到更稳定的结果。</p>
        </div>
      </article>
    </aside>
  </div>
</template>
